import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Lightning, GoogleLogo, Envelope, Lock, User, Flame, Barbell,
  UserCircleGear, Buildings, PersonSimpleRun, CheckCircle, ArrowLeft,
  Users, Heart, Trophy, Storefront, Medal, Cake, ShieldCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { dashboardPathFor } from "@/lib/roles";
import { Input } from "@/components/ui/input";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const handleGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/post-login";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?auto=format&fit=crop&w=1600&q=80";

// Top-level buckets (4 cards) — the merged model.
// Each card either has sub-choices (subs[]) OR maps directly to a primary_role.
const ROLE_BUCKETS = [
  {
    id: "trainee",
    title: "I TRAIN",
    subtitle: "Athlete or self-directed",
    icon: Barbell,
    testid: "role-trainee",
    subs: [
      {
        id: "athlete", primary_role: "athlete",
        title: "I HAVE A COACH", subtitle: "Managed by a coach or academy",
        icon: Barbell, testid: "sub-athlete",
        perks: ["Receive coach-assigned programs", "Message your coach", "Team leaderboards"],
      },
      {
        id: "solo", primary_role: "general_client",
        title: "I TRAIN SOLO", subtitle: "Self-directed fitness",
        icon: PersonSimpleRun, testid: "sub-solo",
        perks: ["AI-built programs", "Log workouts, PRs, goals", "Nutrition & recovery tracking"],
      },
    ],
  },
  {
    id: "coach",
    title: "I COACH",
    subtitle: "Independent coach or trainer",
    icon: UserCircleGear,
    testid: "role-coach",
    subs: [
      {
        id: "strength", primary_role: "strength_coach",
        title: "STRENGTH & CONDITIONING", subtitle: "Barbell + performance focus",
        icon: Barbell, testid: "sub-strength",
        perks: ["1RM board + load calculator", "Autoregulation + deload planner", "Program library + form check"],
      },
      {
        id: "fitness", primary_role: "fitness_trainer",
        title: "FITNESS TRAINER", subtitle: "General fitness + wellness",
        icon: Heart, testid: "sub-fitness",
        perks: ["Client roster management", "Habit + nutrition tracking", "Video form review"],
      },
    ],
  },
  {
    id: "academy",
    title: "I RUN A FACILITY",
    subtitle: "Academy, gym, club, or team",
    icon: Buildings,
    testid: "role-academy",
    subs: [
      {
        id: "academy_admin", primary_role: "academy_admin",
        title: "ACADEMY / GYM ADMIN", subtitle: "Own or manage the facility",
        icon: Storefront, testid: "sub-academy-admin",
        perks: ["Invite coaches & athletes", "Cross-team leaderboards", "Fees + attendance + events"],
      },
      {
        id: "sports_coach", primary_role: "sports_coach",
        title: "SPORTS COACH / GYM TRAINER", subtitle: "Staff at an academy or gym",
        icon: Medal, testid: "sub-sports-coach",
        perks: ["Sport-specific drills", "Match-day playbook", "Team roster management"],
      },
    ],
  },
  {
    id: "parent",
    title: "I'M A PARENT",
    subtitle: "Guardian of a young athlete",
    icon: Users,
    testid: "role-parent",
    primary_role: "parent", // no step 2
  },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  // Steps: "bucket" -> optional "sub" -> "account"
  const [step, setStep] = useState("bucket");
  const [bucketId, setBucketId] = useState(null);
  const [subId, setSubId] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [dob, setDob] = useState("");
  const [guardianEmail, setGuardianEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const bucket = useMemo(() => ROLE_BUCKETS.find((b) => b.id === bucketId), [bucketId]);
  const sub = useMemo(() => bucket?.subs?.find((s) => s.id === subId), [bucket, subId]);
  const resolvedPrimaryRole = sub?.primary_role || bucket?.primary_role || null;

  // Age gate — DOB required on the athlete/"I train" paths.
  const TRAINEE_ROLES = ["athlete", "general_client"];
  const needsDob = TRAINEE_ROLES.includes(resolvedPrimaryRole);
  const computedAge = useMemo(() => {
    if (!dob) return null;
    const d = new Date(dob);
    if (isNaN(d.getTime())) return null;
    const t = new Date();
    let a = t.getFullYear() - d.getFullYear();
    const m = t.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a--;
    return a;
  }, [dob]);
  const isMinor = needsDob && computedAge != null && computedAge < 18;

  const chooseBucket = (b) => {
    setBucketId(b.id);
    setSubId(null);
    if (b.subs?.length) setStep("sub");
    else setStep("account");
  };

  const chooseSub = (s) => {
    setSubId(s.id);
    setStep("account");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!resolvedPrimaryRole) { toast.error("Please pick a role first"); return; }
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (needsDob) {
      if (!dob) { toast.error("Please enter your date of birth"); return; }
      if (computedAge == null || computedAge < 0 || computedAge > 120) { toast.error("Please enter a valid date of birth"); return; }
      if (isMinor && !guardianEmail.trim()) { toast.error("A parent or guardian's email is required for athletes under 18"); return; }
      if (isMinor && guardianEmail.trim().toLowerCase() === email.trim().toLowerCase()) { toast.error("Guardian email must be different from your own"); return; }
    }
    setLoading(true);
    try {
      const extra = {};
      if (needsDob) extra.date_of_birth = dob;
      if (isMinor) extra.guardian_email = guardianEmail.trim();
      const u = await register(email, password, name, resolvedPrimaryRole, extra);
      if (u.is_minor && u.guardian_consent_status === "pending") {
        toast.success("Account created! We've asked your guardian to confirm it.");
      } else {
        toast.success(`Welcome to Edge Training, ${u.name || "champ"}. Let's train.`);
      }
      navigate(dashboardPathFor(u.primary_role));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const headerIcon = sub?.icon || bucket?.icon || Barbell;
  const HeaderIcon = headerIcon;
  const headerTitle = sub?.title || bucket?.title || "";
  const stepIndicator = step === "bucket" ? "STEP 1 OF 2"
    : step === "sub" ? "STEP 2 OF 2"
    : bucket?.subs?.length ? "FINAL STEP" : "STEP 2 OF 2";

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-surface-base" data-testid="register-page">
      {/* LEFT hero */}
      <div className="relative hidden lg:block lg:col-span-3 overflow-hidden landing-dark" data-testid="register-hero-panel">
        <img src={HERO_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover" loading="eager" />
        <div className="absolute inset-0" style={{
          background: "linear-gradient(120deg, rgba(15,23,42,0.72) 0%, rgba(30,58,138,0.60) 55%, rgba(255,107,53,0.30) 100%)",
        }} aria-hidden />
        <div className="absolute inset-0 mix-blend-overlay opacity-30 stripe-bg pointer-events-none" aria-hidden />

        <div className="relative z-10 h-full flex flex-col justify-between p-10 lg:p-14 text-white">
          <div className="flex items-center gap-2">
            <img src="/icon-192.png" alt="Edge Training" className="h-12 w-12 rounded-lg shadow-lg" />
            <span className="font-heading text-xl tracking-tight leading-none text-white">EDGE TRAINING</span>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="energy-dot"></span>
              <span className="label-tiny text-white/90">// JOIN THE ROSTER</span>
            </div>
            <h1 className="font-heading text-5xl lg:text-6xl xl:text-7xl leading-[0.95] mb-6">
              THE <span className="text-[#F0D000]">FIRST REP</span><br />
              STARTS <span className="text-[#F0D000]">TODAY.</span>
            </h1>
            <p className="text-lg text-white/80 max-w-md">
              Pick your path. Get the experience made for you — no bloat, no clutter, just what you need.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4 max-w-lg">
            {[
              { k: "AI PROGRAMS", v: "3/mo" },
              { k: "FREE FOREVER", v: "₹0" },
              { k: "TO ENLIST", v: "60s" },
            ].map((s) => (
              <div key={s.k} className="border-l-2 border-[#F0D000] pl-3">
                <div className="font-heading text-3xl tabular-nums">{s.v}</div>
                <div className="label-tiny text-white/70 text-[10px]">{s.k}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* RIGHT: bucket picker | sub picker | account form */}
      <div className="lg:col-span-2 flex items-center justify-center p-6 lg:p-10 stripe-bg overflow-y-auto">
        <div className="w-full max-w-md tactical-card p-8 lg:p-10 relative">
          <Link to="/" className="flex items-center gap-2 mb-8 lg:hidden">
            <img src="/icon-192.png" alt="Edge Training" className="h-10 w-10 rounded-lg" />
            <span className="font-heading text-lg tracking-tight leading-none text-white">EDGE TRAINING</span>
          </Link>

          {/* STEP 1: Top-level buckets */}
          {step === "bucket" && (
            <div data-testid="register-bucket-picker">
              <div className="mb-6">
                <div className="label-tiny text-brand-accent mb-2 flex items-center gap-2">
                  <Flame size={12} weight="fill" className="text-heat" /> // {stepIndicator}
                </div>
                <h2 className="font-heading text-3xl mb-2">CHOOSE YOUR PATH</h2>
                <p className="text-sm text-text-secondary">
                  Choose your starting profile. You can manage your profiles later in Account Settings.
                </p>
              </div>

              <div className="space-y-3 mb-6">
                {ROLE_BUCKETS.map((b) => {
                  const active = bucketId === b.id;
                  return (
                    <button
                      key={b.id}
                      onClick={() => chooseBucket(b)}
                      data-testid={b.testid}
                      className={`w-full text-left border p-4 transition-all group ${
                        active
                          ? "border-brand-accent bg-brand-accent/5"
                          : "border-white/10 hover:border-brand-accent/50 hover:bg-white/[0.02]"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <b.icon size={26} weight="duotone" className={active ? "text-brand-accent" : "text-text-secondary"} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-heading text-base">{b.title}</span>
                            {b.subs?.length ? (
                              <span className="label-tiny text-text-secondary bg-white/5 px-1.5 py-0.5 border border-white/10">
                                {b.subs.length} OPTIONS
                              </span>
                            ) : null}
                          </div>
                          <div className="text-xs text-text-secondary">{b.subtitle}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <p className="text-sm text-text-secondary text-center mt-6">
                Already enlisted?{" "}
                <Link to="/login" className="text-brand-accent hover:underline font-medium">Sign in</Link>
              </p>
            </div>
          )}

          {/* STEP 2: Sub-picker (only if bucket has subs) */}
          {step === "sub" && bucket?.subs?.length > 0 && (
            <div data-testid="register-sub-picker">
              <button
                onClick={() => { setStep("bucket"); setSubId(null); }}
                className="text-xs label-tiny text-text-secondary hover:text-brand-accent inline-flex items-center gap-1 mb-4"
                data-testid="register-back-to-bucket"
              >
                <ArrowLeft size={12} /> BACK
              </button>
              <div className="mb-6">
                <div className="label-tiny text-brand-accent mb-2 flex items-center gap-2">
                  <bucket.icon size={12} weight="fill" /> // {stepIndicator} · {bucket.title}
                </div>
                <h2 className="font-heading text-3xl mb-2">WHAT&apos;S YOUR SPECIALTY?</h2>
                <p className="text-sm text-text-secondary">
                  Pick the mode that matches how you actually train or work.
                </p>
              </div>

              <div className="space-y-3 mb-6">
                {bucket.subs.map((s) => {
                  const active = subId === s.id;
                  return (
                    <button
                      key={s.id}
                      onClick={() => chooseSub(s)}
                      data-testid={s.testid}
                      className={`w-full text-left border p-4 transition-all group ${
                        active
                          ? "border-brand-accent bg-brand-accent/5"
                          : "border-white/10 hover:border-brand-accent/50 hover:bg-white/[0.02]"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <s.icon size={26} weight="duotone" className={active ? "text-brand-accent" : "text-text-secondary"} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-heading text-base">{s.title}</span>
                            {active && <CheckCircle size={14} weight="fill" className="text-brand-accent" />}
                          </div>
                          <div className="text-xs text-text-secondary">{s.subtitle}</div>
                          <ul className="mt-2 space-y-0.5 text-[11px] text-text-secondary">
                            {s.perks.map((p, i) => <li key={i}>• {p}</li>)}
                          </ul>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP 3: Account form */}
          {step === "account" && resolvedPrimaryRole && (
            <div data-testid="register-account-step">
              <button
                onClick={() => setStep(bucket?.subs?.length ? "sub" : "bucket")}
                className="text-xs label-tiny text-text-secondary hover:text-brand-accent inline-flex items-center gap-1 mb-4"
                data-testid="register-back-to-role"
              >
                <ArrowLeft size={12} /> CHANGE ROLE
              </button>
              <div className="mb-6">
                <div className="label-tiny text-brand-accent mb-2 flex items-center gap-2">
                  <HeaderIcon size={12} weight="fill" /> // {headerTitle}
                </div>
                <h2 className="font-heading text-3xl mb-2">ENLIST</h2>
                <p className="text-sm text-text-secondary">Create your account. Start in seconds.</p>
              </div>

              <button
                onClick={handleGoogleLogin}
                className="w-full mb-6 border border-white/10 hover:border-white/40 hover:bg-white/5 transition-colors px-4 py-3 flex items-center justify-center gap-3"
                data-testid="register-google-button"
              >
                <GoogleLogo size={20} weight="bold" />
                <span className="label-tiny">CONTINUE WITH GOOGLE</span>
              </button>

              <div className="flex items-center gap-4 mb-6">
                <div className="flex-1 h-px bg-white/10" />
                <span className="label-tiny text-text-secondary">OR</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
                <div>
                  <label className="label-tiny block mb-2">NAME</label>
                  <div className="relative">
                    <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                    <Input required value={name} onChange={(e) => setName(e.target.value)} className="pl-10" placeholder="Your name" data-testid="register-name-input" />
                  </div>
                </div>
                <div>
                  <label className="label-tiny block mb-2">EMAIL</label>
                  <div className="relative">
                    <Envelope size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                    <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10" placeholder="you@example.com" data-testid="register-email-input" />
                  </div>
                </div>
                <div>
                  <label className="label-tiny block mb-2">PASSWORD</label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                    <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10" placeholder="Min 6 characters" data-testid="register-password-input" />
                  </div>
                </div>
                {needsDob && (
                  <div data-testid="register-dob-block">
                    <label className="label-tiny block mb-2">DATE OF BIRTH</label>
                    <div className="relative">
                      <Cake size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                      <Input
                        type="date"
                        required
                        value={dob}
                        max={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => setDob(e.target.value)}
                        className="pl-10"
                        data-testid="register-dob-input"
                      />
                    </div>
                    {computedAge != null && computedAge >= 0 && (
                      <p className="text-[11px] text-text-secondary mt-1">
                        {isMinor
                          ? `Under 18 — a parent or guardian must confirm your account before coach linking, messaging and leaderboards unlock.`
                          : `Age ${computedAge}`}
                      </p>
                    )}
                  </div>
                )}
                {isMinor && (
                  <div data-testid="register-guardian-block">
                    <label className="label-tiny block mb-2">PARENT / GUARDIAN EMAIL</label>
                    <div className="relative">
                      <ShieldCheck size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                      <Input
                        type="email"
                        required
                        value={guardianEmail}
                        onChange={(e) => setGuardianEmail(e.target.value)}
                        className="pl-10"
                        placeholder="guardian@example.com"
                        data-testid="register-guardian-email-input"
                      />
                    </div>
                    <p className="text-[11px] text-text-secondary mt-1">
                      We'll ask them to confirm your account. If they already use Edge Training they'll get a notification; otherwise ask them to sign up with this email.
                    </p>
                  </div>
                )}
                <button type="submit" disabled={loading} className="btn-kinetic w-full py-3 text-xs label-tiny tracking-widest disabled:opacity-50" data-testid="register-submit-button">
                  {loading ? "CREATING ACCOUNT..." : "ENLIST NOW"}
                </button>
                <p className="text-[11px] text-text-secondary text-center leading-snug" data-testid="register-legal-consent">
                  By enlisting you agree to our{" "}
                  <Link to="/terms" className="text-brand-accent hover:underline">Terms</Link> and{" "}
                  <Link to="/privacy" className="text-brand-accent hover:underline">Privacy Policy</Link>.
                </p>
              </form>

              <p className="text-sm text-text-secondary text-center mt-6">
                Already enlisted?{" "}
                <Link to="/login" className="text-brand-accent hover:underline font-medium">Sign in</Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
